import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { AngularMaterialModule } from '../angular-material.module';
import { TournamentPublicGameStats, TournamentPublicPlayerStats } from './tournament.model';
import { TournamentService } from './tournament.service';

export interface TournamentGameStatsDialogData {
  tournamentId: string;
  scheduleId: string;
}

@Component({
  selector: 'app-tournament-game-stats-dialog',
  standalone: true,
  imports: [CommonModule, AngularMaterialModule],
  templateUrl: './tournament-game-stats-dialog.component.html',
  styleUrls: ['./tournament-game-stats-dialog.component.css'],
})
export class TournamentGameStatsDialogComponent implements OnInit, OnDestroy {
  stats: TournamentPublicGameStats | null = null;
  loading = true;
  error = '';
  private request: Subscription | null = null;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private inFlight = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: TournamentGameStatsDialogData,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly tournamentsService: TournamentService,
    readonly dialogRef: MatDialogRef<TournamentGameStatsDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.loadStats();
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stopRefresh();
    this.request?.unsubscribe();
  }

  retry(): void {
    this.loadStats();
  }

  playerLabel(player: TournamentPublicPlayerStats): string {
    return player.display_name || (player.jersey ? `#${player.jersey}` : 'Player');
  }

  periodLabel(): string {
    const period = String(this.stats?.game.period || '').toUpperCase();
    if (period === 'H1') return '1st Half';
    if (period === 'H2') return '2nd Half';
    if (/^Q[1-4]$/.test(period)) return period;
    if (period === 'OT') return 'OT 1';
    if (/^OT\d+$/.test(period)) return `OT ${period.slice(2)}`;
    return 'In progress';
  }

  clockLabel(): string {
    return /^\d{1,2}:[0-5]\d$/.test(String(this.stats?.game.game_clock || ''))
      ? String(this.stats?.game.game_clock)
      : '--:--';
  }

  hasAnyStats(): boolean {
    return !!this.stats && (this.stats.home.has_stats || this.stats.away.has_stats);
  }

  private loadStats(): void {
    if (this.inFlight) return;
    this.inFlight = true;
    this.loading = !this.stats;
    this.error = '';
    this.request = this.tournamentsService.getPublicGameStats(this.data.tournamentId, this.data.scheduleId).subscribe({
      next: (stats) => {
        this.stats = stats;
        this.loading = false;
        this.inFlight = false;
        this.syncRefresh();
      },
      error: () => {
        this.loading = false;
        this.inFlight = false;
        this.error = this.stats
          ? 'Live stats could not be refreshed. Try again.'
          : 'Game stats could not be loaded. Try again.';
        this.syncRefresh();
      },
    });
  }

  private syncRefresh(): void {
    if (!this.stats?.game.is_live || this.document.hidden) {
      this.stopRefresh();
      return;
    }
    if (this.refreshTimer === null) {
      this.refreshTimer = setInterval(() => this.loadStats(), 5000);
    }
  }

  private stopRefresh(): void {
    if (this.refreshTimer !== null) clearInterval(this.refreshTimer);
    this.refreshTimer = null;
  }

  private readonly onVisibilityChange = (): void => {
    if (this.document.hidden) this.stopRefresh();
    else this.syncRefresh();
  };
}
